import type { AuthUser } from '@/store/auth.store'

/**
 * Claims carried by the tap-api access token (see tap-api signAccessToken).
 * These are read client-side for display only — the server re-verifies the
 * signature on every request, so a tampered token simply fails server checks.
 */
export interface AccessTokenClaims {
  sub: string
  authUserId: string
  tenantId: string
  email: string
  role: string
  iat?: number
  exp?: number
}

/** Base64url-decode and JSON-parse a JWT payload. Returns null if malformed. */
export function decodeJwt(token: string): AccessTokenClaims | null {
  const parts = token.split('.')
  if (parts.length !== 3) return null
  const payload = parts[1]
  if (!payload) return null
  try {
    const base64 = payload.replace(/-/g, '+').replace(/_/g, '/')
    const padded = base64.padEnd(base64.length + ((4 - (base64.length % 4)) % 4), '=')
    const json = decodeURIComponent(
      atob(padded)
        .split('')
        .map((c) => `%${c.charCodeAt(0).toString(16).padStart(2, '0')}`)
        .join(''),
    )
    return JSON.parse(json) as AccessTokenClaims
  } catch {
    return null
  }
}

/** Whether the token is expired (with an optional skew in seconds). */
export function isTokenExpired(
  claims: AccessTokenClaims,
  skewSeconds = 0,
  now = Date.now(),
): boolean {
  if (claims.exp === undefined) return false
  return claims.exp * 1000 <= now + skewSeconds * 1000
}

/**
 * Build the persisted AuthUser from access-token claims. The token has no name
 * or tenant name; the name defaults to the email's local part and is enriched
 * from /tenants/current + /users/{id} once those load (Stage 3). Permissions
 * start empty and are populated by the app shell.
 */
export function authUserFromClaims(
  claims: AccessTokenClaims,
  overrides?: Partial<AuthUser>,
): AuthUser {
  const fallbackName = claims.email.split('@')[0] ?? claims.email
  return {
    id: claims.sub,
    email: claims.email,
    name: overrides?.name ?? fallbackName,
    role: claims.role,
    tenantId: claims.tenantId,
    tenantName: overrides?.tenantName ?? '',
    permissions: overrides?.permissions ?? [],
  }
}
